import { Alert, Button, Form, notification, Row, Spin, Steps } from "antd";
import React, { useEffect, useState, useContext } from "react";
import { MainContext } from "@Context/provider";
import { Redirect, useLocation } from "react-router";

import RegistrationPractice from "./RegistrationPractice";
import Security from "./Security";
import RegistrationDetails from "./RegistrationDetails";
import SessionManager from "../../core/session";
import { IRegisterDetails } from "@Models/IRegisterDetails";
import { ILoginDetails } from "@Models/ILoginDetails";
import AccountService from "@Services/AccountService";

import "@Styles/pages/registrationPage.scss";
import { ViewMode } from "@Context/state";
import { useGetLatestTerms } from "@Components/shared/queries";
import { TermsTypes } from "@Constants";

export interface StepInterface {
    title: string;
}

const Registration: React.FC = () => {
    const [status, setStatus] = useState<string>("pending");
    const [verifiedFullName, setVerifiedFullName] = useState<string>("");
    const [current, setCurrent] = useState<number>(0);
    const [terms, setTerms] = useState(false);
    const [formSubmitted, setFormSubmitted] = useState(false);
    const [loginSuccess, setLoginSuccess] = useState(false);
    const { ui: uiState } = useContext(MainContext);
    const isDesktop = uiState.mode === ViewMode.desktop;
    const textColor = isDesktop ? { color: "black" } : { color: "white" };

    const userDetails = ["fullName", "nickName", "dateOfBirth", "countryCode", "gender"];
    const security = ["discoverability", "login", "password", "confirmPassword"];
    const [form] = Form.useForm();
    const formLayout = { labelCol: { span: 8 }, wrapperCol: { span: 8 } };
    const location = useLocation();
    const { Step } = Steps;
    const steps: StepInterface[] = [{ title: "Your Details" }, { title: "About You" }, { title: "Security" }];
    const { data: termsData, isLoading: loadingTerms } = useGetLatestTerms(TermsTypes.Terms);
    const { data: privacyData, isLoading: loadingPrivacy } = useGetLatestTerms(TermsTypes.PrivacyPolicy);

    // Map the data and loading states to each term type
    const termsQueries = {
        [TermsTypes.Terms]: { data: termsData, isLoading: loadingTerms },
        [TermsTypes.PrivacyPolicy]: { data: privacyData, isLoading: loadingPrivacy },
    };

    /**
     * Extract the registration payload from the url and check whether user account exists.
     */
    const verifyRegistrationPayload = async () => {
        if (!location.search.includes("?account=")) {
            setStatus("failure");
            return;
        }
        const payload = extractPayload(location.search);
        if (payload.length < 8) {
            setStatus("failure");
            return;
        }

        const service = new AccountService();
        const result = await service.verifyAccount(payload);

        if (!result.hasErrors && result.value) {
            setVerifiedFullName(result.value);
            setStatus("success");
        } else {
            console.error("Error verifying registration payload:", result.errors);
            setStatus("failure");
        }
    };

    /**
     * Submit payload from the registration form and submit it to API to complete the current user's registration procedure.
     * @param formPayload data collected with register form, Object (FormPayloadSchema)
     */
    const completeUserRegistration = async (formPayload: IRegisterDetails) => {
        const service = new AccountService();
        const result = await service.completeRegistration(formPayload);

        if (!result.hasErrors) {
            setStatus("complete");
            loginUser({ login: result.value?.email, password: formPayload.password }).catch((error) => {
                console.error("Error in loginUser:", error);
            });
        } else {
            console.error("Error in completeUserRegistration:", result.errors);
        }
    };

    /**
     * Extract the registration payload from current url.
     */
    const extractPayload = (search: string) => {
        return search.split("?account=")[1];
    };

    /**
     * Log user in.
     */
    const loginUser = async (login: ILoginDetails) => {
        const service = new AccountService();
        const result = await service.login(login);
        if (!result.hasErrors) {
            setLoginSuccess(true);
        } else {
            notification.error({ message: "Login error" });
        }
    };

    /**
     * Returns an array containing all errors for current register form section
     * @param form object containing all values and methods regarding register form (ant design)
     * @param sections array of strings containing all fields of current section
     * @returns array of arrays containing all errors ([['field1', 'value1'], ['field2', 'value2']])
     */
    const getFieldErrors = (form, sections) => {
        const fields = Object.entries(form.getFieldsValue()).filter((field) => sections.includes(field[0]));
        return fields.filter((field) => !field[1]);
    };

    /**
     * Next form page.
     * @param currentStep
     */
    const next = async (currentStep: number) => {
        // if (currentStep === 1) {
        //     setCurrent(current + 1);
        // } else {
        // const sections = currentStep === 0 ? userDetails : security;
        let sections;
        if (currentStep === 0) {
            sections = userDetails;
        } else if (currentStep === 1) {
            sections = ["expectedGraduationYear"];
        } else {
            sections = security;
        }

        await form.validateFields(sections).catch(() => {
            // catch the error only | no need to handle it here
        });

        // TODO: after antd update handle errors in above promise
        const errors = getFieldErrors(form, sections);

        if (errors.length === 0) {
            setCurrent(current + 1);
        }
        //}
    };

    /**
     * Previous form page.
     */
    const prev = () => {
        setCurrent(current - 1);
    };
    /**
     * Form completion.
     * @param values
     */
    const done = async (values) => {
        setFormSubmitted(true);

        const currentValues = { ...values };
        delete currentValues.confirmPassword;

        // convert date of birth to correct format
        const dateOfBirth = currentValues.dateOfBirth?.toISOString();

        // convert expected graduation year to an integer
        const expectedGraduationYear = currentValues.expectedGraduationYear?.year();

        // extract registration payload from the url
        const registrationPayload = extractPayload(location.search);
        const finalPayload = { ...currentValues, dateOfBirth, expectedGraduationYear, registrationPayload };
        await completeUserRegistration(finalPayload);
    };

    /**
     * Verify user registration payload from URL on page load.
     */
    useEffect(() => {
        verifyRegistrationPayload().catch((error) => {
            console.error("Error in verifyRegistrationPayload:", error);
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return SessionManager.isAuthenticated && loginSuccess ? (
        <Redirect to="/" />
    ) : status === "complete" ? (
        <>
            <h3 style={textColor}>Complete.</h3>
            <h5 style={textColor}>Thanks for registering. We're logging you into Totum.</h5>
            <Spin size={"large"} />
        </>
    ) : status === "pending" ? (
        <>
            <h3 style={textColor}>Please wait.</h3>
            <h5 style={textColor}>It may take a few seconds to verify your account.</h5>
            <Spin size={"large"} />
        </>
    ) : status === "success" ? (
        <>
            <h3 style={textColor}>Welcome, {verifiedFullName}.</h3>
            <h5 style={textColor}>We just need a few more details from you to complete your registration.</h5>
            <Steps current={current}>
                {steps.map((step, index) => {
                    return <Step key={index} title={step.title} className={isDesktop ? "black-label" : null} />;
                })}
            </Steps>
            <Form
                form={form}
                className="registration-form"
                {...formLayout}
                onFinish={done}
                initialValues={{
                    fullName: verifiedFullName,
                    nickName: verifiedFullName.split(" ")[0],
                    specialty: null,
                    medicalSchool: null,
                    organisation: null,
                    yearsTraining: null,
                    expectedGraduationYear: null,
                    termsAndConditions: terms,
                    discoverability: true,
                }}
            >
                <RegistrationDetails current={current} />
                <RegistrationPractice current={current} setCurrent={setCurrent} />
                <Security
                    form={form}
                    current={current}
                    steps={steps}
                    totumTerms={terms}
                    updateTerms={setTerms}
                    termsQueries={termsQueries}
                />
                <Row>
                    <Form.Item>
                        {current === 0 && (
                            <span>
                                <Button type="primary" onClick={() => next(current)}>
                                    Next
                                </Button>
                            </span>
                        )}
                        {current !== 0 && current !== steps.length - 1 && (
                            <span style={{ display: "flex", gap: 10 }}>
                                <Button className="secondary" type="default" onClick={() => prev()}>
                                    Prev
                                </Button>
                                <Button type="primary" onClick={() => next(current)}>
                                    Next
                                </Button>
                            </span>
                        )}
                        {current === steps.length - 1 && (
                            <div style={{ minWidth: "200px", display: "flex", gap: 10 }}>
                                <Button className="secondary" onClick={() => prev()}>
                                    Prev
                                </Button>
                                <Button
                                    disabled={!terms}
                                    className="done-button"
                                    type="primary"
                                    htmlType="submit"
                                    loading={formSubmitted}
                                >
                                    Done
                                </Button>
                            </div>
                        )}
                    </Form.Item>
                </Row>
            </Form>
        </>
    ) : (
        <>
            <div className={"spacer"}>&nbsp;</div>
            <Alert
                message="Sorry, we were unable to verify your account. Please contact your vendor or administrator for additional support."
                showIcon
                type="error"
            />
        </>
    );
};

export default Registration;
